import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  fetchFamilyMember,
  editFamilyMember,
  canEdit, // from your api
} from '../../../../Utilities/api';
import { StateContext } from '../../../../StateProvider';
import { BackButton } from '../../../../Utilities/components/Buttons';
import './PetInfo.css';

/** ES6-safe helpers */
function piVal(v, fallback) {
  if (v === null || v === undefined || v === '') {
    return (typeof fallback !== 'undefined' ? fallback : 'N/A');
  }
  return v;
}
function piPrettyDate(iso) {
  if (!iso) return 'N/A';
  try { return new Date(iso).toLocaleDateString(); } catch (e) { return 'Invalid Date'; }
}
function piPhone(v) {
  if (!v) return 'N/A';
  var cleaned = String(v).replace(/\D/g, '');
  var m = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  return m ? (m[1] + '-' + m[2] + '-' + m[3]) : v;
}

const PetInfo = () => {
  const { petId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useContext(StateContext) || {};

  const allowEdit = !!canEdit && canEdit(currentUser); // admin | manager | reception

  const [pet, setPet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);

  const [editing, setEditing] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [form, setForm] = useState({
    name: '',
    type: '',
    breed: '',
    age: '',
    dob: '',
    vaccination: '',
    daycare: false,
    image: ''
  });

  useEffect(function onMount() {
    async function run() {
      try {
        const data = await fetchFamilyMember(petId);
        if (!data) throw new Error('Pet not found');
        setPet(data);
        setForm({
          name: data.name || '',
          type: data.type || '',
          breed: data.breed || '',
          age: (typeof data.age === 'number') ? String(data.age) : (data.age || ''),
          dob: data.dob ? new Date(data.dob).toISOString().slice(0, 10) : '',
          vaccination: data.vaccination || '',
          daycare: !!data.daycare,
          image: data.image || ''
        });
      } catch (err) {
        setError((err && err.message) ? err.message : 'Failed to fetch pet data');
      } finally {
        setLoading(false);
      }
    }
    run();
  }, [petId]);

  async function handleSave() {
    if (!allowEdit) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        type: form.type,
        breed: form.breed,
        age: form.age ? Number(form.age) : null,
        dob: form.dob ? new Date(form.dob).toISOString() : null,
        vaccination: form.vaccination,
        daycare: !!form.daycare,
        image: form.image
      };
      const updated = await editFamilyMember(petId, payload);
      setPet(Object.assign({}, pet, updated));
      setEditing(false);
    } catch (e) {
      alert(e && e.message ? e.message : 'Failed to save pet');
    } finally {
      setSaving(false);
    }
  }

  function handleOwnerClick(owner) {
    navigate('/console/userInfo/' + owner.uid);
    try { window.scrollTo(0, 0); } catch (e) {}
  }

  if (loading) {
    return (
      <div className="pi-loading">
        <p className="pi-loading-text">Loading pet information...</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="pi-error">
        Error: {error}
        <button className="pi-error-btn" onClick={function(){ navigate('/console/pets'); }}>
          Back to List
        </button>
      </div>
    );
  }
  if (!pet) return <div className="pi-missing">Pet not found.</div>;

  return (
    <div className="pi-page">
      {/* Top bar */}
      <div className="pi-topbar">
        <BackButton />
        <div className="pi-actions">
          <button
            className="pi-btn pi-btn-pink"
            onClick={function(){ navigate('/console/userBookingInfo/' + pet.User.uid); }}
          >
            Bookings
          </button>
          <button
            className="pi-btn pi-btn-purple"
            onClick={function(){ navigate('/console/userPaymentHistory/' + pet.User.uid); }}
          >
            Transactions
          </button>
        </div>
      </div>

      {/* Summary Card */}
      <section className="pi-card pi-summary">
        <div className="pi-thumb">
          {(!editing && pet.image) ? (
            <img src={pet.image} alt={'Photo of ' + piVal(pet.name, 'Pet')} />
          ) : (
            <div className="pi-thumb-fallback">{(piVal(pet.name, 'P') + '').charAt(0)}</div>
          )}
        </div>

        <div className="pi-summary-main">
          <div className="pi-summary-header">
            {editing ? (
              <input
                className="pi-input pi-input-lg"
                value={form.name}
                onChange={function(e){ setForm(Object.assign({}, form, { name: e.target.value })); }}
                placeholder="Pet name"
              />
            ) : (
              <h1 className="pi-title">{piVal(pet.name, 'Pet')}</h1>
            )}

            {allowEdit && (
              <div className="pi-edit-controls">
                {!editing && (
                  <button className="pi-btn pi-btn-ghost" onClick={function(){ setEditing(true); }}>
                    Edit
                  </button>
                )}
                {editing && (
                  <React.Fragment>
                    <button className="pi-btn pi-btn-success" onClick={handleSave} disabled={saving}>
                      {saving ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      className="pi-btn pi-btn-ghost"
                      onClick={function(){
                        setEditing(false);
                        setForm({
                          name: pet.name || '',
                          type: pet.type || '',
                          breed: pet.breed || '',
                          age: (typeof pet.age === 'number') ? String(pet.age) : (pet.age || ''),
                          dob: pet.dob ? new Date(pet.dob).toISOString().slice(0, 10) : '',
                          vaccination: pet.vaccination || '',
                          daycare: !!pet.daycare,
                          image: pet.image || ''
                        });
                      }}
                    >
                      Cancel
                    </button>
                  </React.Fragment>
                )}
              </div>
            )}
          </div>

          {/* Image URL editor */}
          {editing && (
            <div className="pi-field">
              <label className="pi-label">Image URL</label>
              <input
                className="pi-input"
                value={form.image}
                onChange={function(e){ setForm(Object.assign({}, form, { image: e.target.value })); }}
                placeholder="https://..."
              />
            </div>
          )}

          {/* Key facts */}
          <div className="pi-kv-grid">
            <div className="pi-kv">
              <span className="pi-kv-label">Type</span>
              <span className="pi-kv-value">
                {editing ? (
                  <select
                    className="pi-input"
                    value={form.type}
                    onChange={function(e){ setForm(Object.assign({}, form, { type: e.target.value })); }}
                  >
                    <option value="">Select</option>
                    <option value="Dog">Dog</option>
                    <option value="Cat">Cat</option>
                    <option value="Other">Other</option>
                  </select>
                ) : (
                  <span className="pi-badge pi-badge-neutral">{piVal(pet.type)}</span>
                )}
              </span>
            </div>

            <div className="pi-kv">
              <span className="pi-kv-label">Breed</span>
              <span className="pi-kv-value">
                {editing ? (
                  <input
                    className="pi-input"
                    value={form.breed}
                    onChange={function(e){ setForm(Object.assign({}, form, { breed: e.target.value })); }}
                  />
                ) : piVal(pet.breed)}
              </span>
            </div>

            <div className="pi-kv">
              <span className="pi-kv-label">Age</span>
              <span className="pi-kv-value">
                {editing ? (
                  <input
                    className="pi-input"
                    type="number"
                    min="0"
                    value={form.age}
                    onChange={function(e){ setForm(Object.assign({}, form, { age: e.target.value })); }}
                  />
                ) : piVal(pet.age)}
              </span>
            </div>

            <div className="pi-kv">
              <span className="pi-kv-label">DOB</span>
              <span className="pi-kv-value">
                {editing ? (
                  <input
                    className="pi-input"
                    type="date"
                    value={form.dob}
                    onChange={function(e){ setForm(Object.assign({}, form, { dob: e.target.value })); }}
                  />
                ) : piPrettyDate(pet.dob)}
              </span>
            </div>

            <div className="pi-kv pi-kv-span2">
              <span className="pi-kv-label">Vaccination</span>
              <span className="pi-kv-value">
                {editing ? (
                  <input
                    className="pi-input"
                    value={form.vaccination}
                    onChange={function(e){ setForm(Object.assign({}, form, { vaccination: e.target.value })); }}
                    placeholder="https://..."
                  />
                ) : (
                  pet.vaccination
                    ? <a className="pi-link" href={pet.vaccination} target="_blank" rel="noopener noreferrer">View</a>
                    : 'N/A'
                )}
              </span>
            </div>

            <div className="pi-kv">
              <span className="pi-kv-label">Daycare</span>
              <span className="pi-kv-value">
                {editing ? (
                  <select
                    className="pi-input"
                    value={form.daycare ? 'Yes' : 'No'}
                    onChange={function(e){ setForm(Object.assign({}, form, { daycare: e.target.value === 'Yes' })); }}
                  >
                    <option value="No">No</option>
                    <option value="Yes">Yes</option>
                  </select>
                ) : (
                  <span className={pet.daycare ? 'pi-tag pi-tag-ok' : 'pi-tag pi-tag-no'}>
                    {pet.daycare ? 'Yes' : 'No'}
                  </span>
                )}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Owner Card */}
      {pet.User && (
        <section className="pi-card">
          <h2 className="pi-section-title">Owner</h2>
          <div
            className="pi-owner"
            onClick={function(){ handleOwnerClick(pet.User); }}
            role="button"
            tabIndex={0}
            onKeyDown={function(e){ if (e.key === 'Enter') handleOwnerClick(pet.User); }}
          >
            <div className="pi-owner-col">
              <div className="pi-kv"><span className="pi-kv-label">Name</span><span className="pi-kv-value">{piVal(pet.User.username)}</span></div>
              <div className="pi-kv"><span className="pi-kv-label">Email</span><span className="pi-kv-value">{piVal(pet.User.email)}</span></div>
            </div>
            <div className="pi-owner-col">
              <div className="pi-kv"><span className="pi-kv-label">Phone</span><span className="pi-kv-value">{piPhone(pet.User.phone)}</span></div>
              <div className="pi-kv"><span className="pi-kv-label">Store</span><span className="pi-kv-value">{piVal(pet.User.storeLocation)}</span></div>
            </div>
          </div>
        </section>
      )}

      {/* Memberships */}
      {pet.Memberships && pet.Memberships.length > 0 && (
        <section className="pi-card">
          <h2 className="pi-section-title">Memberships</h2>
          <div className="pi-members-grid">
            {pet.Memberships.map(function(m, idx){
              return (
                <div className="pi-member-card" key={idx}>
                  <div className="pi-kv"><span className="pi-kv-label">Start Date</span><span className="pi-kv-value">{m.startDate ? piPrettyDate(m.startDate) : 'N/A'}</span></div>
                  <div className="pi-kv"><span className="pi-kv-label">Daycare</span><span className="pi-kv-value">{m.membershipType ? <span className="pi-badge pi-badge-ok">{m.membershipType}</span> : <span className="pi-badge pi-badge-no">N/A</span>}</span></div>
                  <div className="pi-kv"><span className="pi-kv-label">Benefits</span><span className="pi-kv-value">{piVal(m.benefits)}</span></div>
                  <div className="pi-kv"><span className="pi-kv-label">Daycare Remaining</span><span className="pi-kv-value">{piVal(m.daycareRemaining)}</span></div>
                  <div className="pi-kv"><span className="pi-kv-label">Grooming</span><span className="pi-kv-value">{m.groomingMembershipType ? <span className="pi-badge pi-badge-ok">{m.groomingMembershipType}</span> : <span className="pi-badge pi-badge-no">N/A</span>}</span></div>
                  <div className="pi-kv"><span className="pi-kv-label">Grooming Remaining</span><span className="pi-kv-value">{piVal(m.groomingRemaining)}</span></div>
                  <div className="pi-kv"><span className="pi-kv-label">Bath Remaining</span><span className="pi-kv-value">{piVal(m.bathRemaining)}</span></div>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
};

export default PetInfo;
